// UGOT Pro 관리자 API(pay /api/v1/admin/apps/ugot-pro) 응답 타입과 엄격한 정규화.
// 스키마 원본: packages/api-specs/services/payment/schemas/admin/ugot-pro.yml
// pay 는 시각을 UTC 로, 오프셋 없이 보낸다("2027-10-12T09:00:00"). 표시는 ugotProFormat.ts 가 맡는다.

export type UgotProGrantState = 'ACTIVE' | 'EXPIRED' | 'REVOKED';

export interface UgotProGrant {
  userId: string;
  state: UgotProGrantState;
  active: boolean;
  expiresAt: string;
  note: string;
  grantedBy: string;
  grantedAt: string;
  revokedBy: string | null;
  revokedAt: string | null;
  updatedAt: string;
}

export interface UgotProGrantList {
  active: UgotProGrant[];
  recentlyEnded: UgotProGrant[];
}

export interface UgotProAdminSubscription {
  subscriptionId: number;
  planCode: string | null;
  status: string;
  paddleSubscriptionId: string | null;
  orderId: string | null;
  startDate: string | null;
  nextBillingDate: string | null;
  endDate: string | null;
  autoRenewing: boolean | null;
  latestTransactionId: string | null;
  refundedTransactionId: string | null;
}

export interface UgotProAdminOrder {
  orderId: string;
  planCode: string | null;
  status: string;
  amount: number | null;
  currency: string | null;
  paymentMethod: string | null;
  transactionId: string | null;
  paymentDate: string | null;
  createdAt: string | null;
}

export type UgotProRefundStatus = 'PENDING' | 'APPROVED' | 'PROVIDER_PENDING' | 'REJECTED' | 'PROCESSED' | 'FAILED';

export interface UgotProRefundRequest {
  refundRequestId: number;
  orderId: string;
  userId: string;
  status: UgotProRefundStatus;
  statusLabel: string;
  refundType: string | null;
  originalAmount: number | null;
  refundAmount: number | null;
  currency: string | null;
  reason: string | null;
  policyEvidence: string | null;
  providerTransactionId: string | null;
  adjustmentId: string | null;
  adminNotes: string | null;
  processedBy: string | null;
  createdAt: string | null;
  processedAt: string | null;
}

export interface UgotProBilling {
  userId: string;
  grant: UgotProGrant | null;
  subscriptions: UgotProAdminSubscription[];
  orders: UgotProAdminOrder[];
  refundRequests: UgotProRefundRequest[];
}

export interface UgotProCancelResult {
  subscriptionId: number;
  status: string;
  immediate: boolean;
  providerCanceled: boolean;
  effectiveAt: string | null;
  message: string;
}

export interface UgotProRefundActionResult {
  refund: UgotProRefundRequest;
  orderStatus: string | null;
  message: string;
}

const GRANT_STATES: readonly UgotProGrantState[] = ['ACTIVE', 'EXPIRED', 'REVOKED'];
const REFUND_STATUSES: readonly UgotProRefundStatus[] = [
  'PENDING', 'APPROVED', 'PROVIDER_PENDING', 'REJECTED', 'PROCESSED', 'FAILED',
];

const fail = (field: string): never => {
  throw new Error(`UGOT Pro 응답의 ${field} 값이 올바르지 않습니다.`);
};

const record = (value: unknown, field: string): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fail(field);
  return value as Record<string, unknown>;
};

const text = (value: unknown, field: string): string => (typeof value === 'string' ? value : fail(field));

const optionalText = (value: unknown, field: string): string | null => {
  if (value === null || value === undefined) return null;
  return text(value, field);
};

const integer = (value: unknown, field: string): number => (
  typeof value === 'number' && Number.isInteger(value) ? value : fail(field)
);

const optionalInteger = (value: unknown, field: string): number | null => {
  if (value === null || value === undefined) return null;
  return integer(value, field);
};

const optionalBoolean = (value: unknown, field: string): boolean | null => {
  if (value === null || value === undefined) return null;
  return typeof value === 'boolean' ? value : fail(field);
};

const list = <T>(value: unknown, field: string, item: (entry: unknown, path: string) => T): T[] => {
  if (!Array.isArray(value)) return fail(field);
  return value.map((entry, index) => item(entry, `${field}[${index}]`));
};

export const normalizeUgotProGrant = (payload: unknown, field = 'grant'): UgotProGrant => {
  const row = record(payload, field);
  const state = text(row['state'], `${field}.state`) as UgotProGrantState;
  if (!GRANT_STATES.includes(state)) fail(`${field}.state`);
  if (typeof row['active'] !== 'boolean' || row['active'] !== (state === 'ACTIVE')) fail(`${field}.active`);
  return {
    userId: text(row['userId'], `${field}.userId`),
    state,
    active: row['active'] as boolean,
    expiresAt: text(row['expiresAt'], `${field}.expiresAt`),
    note: text(row['note'], `${field}.note`),
    grantedBy: text(row['grantedBy'], `${field}.grantedBy`),
    grantedAt: text(row['grantedAt'], `${field}.grantedAt`),
    revokedBy: optionalText(row['revokedBy'], `${field}.revokedBy`),
    revokedAt: optionalText(row['revokedAt'], `${field}.revokedAt`),
    updatedAt: text(row['updatedAt'], `${field}.updatedAt`),
  };
};

export const normalizeUgotProGrantList = (payload: unknown): UgotProGrantList => {
  const row = record(payload, 'root');
  return {
    active: list(row['active'], 'active', normalizeUgotProGrant),
    recentlyEnded: list(row['recentlyEnded'], 'recentlyEnded', normalizeUgotProGrant),
  };
};

const normalizeSubscription = (payload: unknown, field: string): UgotProAdminSubscription => {
  const row = record(payload, field);
  return {
    subscriptionId: integer(row['subscriptionId'], `${field}.subscriptionId`),
    planCode: optionalText(row['planCode'], `${field}.planCode`),
    status: text(row['status'], `${field}.status`),
    paddleSubscriptionId: optionalText(row['paddleSubscriptionId'], `${field}.paddleSubscriptionId`),
    orderId: optionalText(row['orderId'], `${field}.orderId`),
    startDate: optionalText(row['startDate'], `${field}.startDate`),
    nextBillingDate: optionalText(row['nextBillingDate'], `${field}.nextBillingDate`),
    endDate: optionalText(row['endDate'], `${field}.endDate`),
    autoRenewing: optionalBoolean(row['autoRenewing'], `${field}.autoRenewing`),
    latestTransactionId: optionalText(row['latestTransactionId'], `${field}.latestTransactionId`),
    refundedTransactionId: optionalText(row['refundedTransactionId'], `${field}.refundedTransactionId`),
  };
};

const normalizeOrder = (payload: unknown, field: string): UgotProAdminOrder => {
  const row = record(payload, field);
  return {
    orderId: text(row['orderId'], `${field}.orderId`),
    planCode: optionalText(row['planCode'], `${field}.planCode`),
    status: text(row['status'], `${field}.status`),
    amount: optionalInteger(row['amount'], `${field}.amount`),
    currency: optionalText(row['currency'], `${field}.currency`),
    paymentMethod: optionalText(row['paymentMethod'], `${field}.paymentMethod`),
    transactionId: optionalText(row['transactionId'], `${field}.transactionId`),
    paymentDate: optionalText(row['paymentDate'], `${field}.paymentDate`),
    createdAt: optionalText(row['createdAt'], `${field}.createdAt`),
  };
};

export const normalizeUgotProRefundRequest = (payload: unknown, field = 'refund'): UgotProRefundRequest => {
  const row = record(payload, field);
  const status = text(row['status'], `${field}.status`) as UgotProRefundStatus;
  if (!REFUND_STATUSES.includes(status)) fail(`${field}.status`);
  return {
    refundRequestId: integer(row['refundRequestId'], `${field}.refundRequestId`),
    orderId: text(row['orderId'], `${field}.orderId`),
    userId: text(row['userId'], `${field}.userId`),
    status,
    statusLabel: text(row['statusLabel'], `${field}.statusLabel`),
    refundType: optionalText(row['refundType'], `${field}.refundType`),
    originalAmount: optionalInteger(row['originalAmount'], `${field}.originalAmount`),
    refundAmount: optionalInteger(row['refundAmount'], `${field}.refundAmount`),
    currency: optionalText(row['currency'], `${field}.currency`),
    reason: optionalText(row['reason'], `${field}.reason`),
    policyEvidence: optionalText(row['policyEvidence'], `${field}.policyEvidence`),
    providerTransactionId: optionalText(row['providerTransactionId'], `${field}.providerTransactionId`),
    adjustmentId: optionalText(row['adjustmentId'], `${field}.adjustmentId`),
    adminNotes: optionalText(row['adminNotes'], `${field}.adminNotes`),
    processedBy: optionalText(row['processedBy'], `${field}.processedBy`),
    createdAt: optionalText(row['createdAt'], `${field}.createdAt`),
    processedAt: optionalText(row['processedAt'], `${field}.processedAt`),
  };
};

export const normalizeUgotProBilling = (payload: unknown): UgotProBilling => {
  const row = record(payload, 'root');
  return {
    userId: text(row['userId'], 'userId'),
    grant: row['grant'] === null || row['grant'] === undefined ? null : normalizeUgotProGrant(row['grant']),
    subscriptions: list(row['subscriptions'], 'subscriptions', normalizeSubscription),
    orders: list(row['orders'], 'orders', normalizeOrder),
    refundRequests: list(row['refundRequests'], 'refundRequests', normalizeUgotProRefundRequest),
  };
};

export const normalizeUgotProRefundRequestList = (payload: unknown): UgotProRefundRequest[] => (
  list(record(payload, 'root')['items'], 'items', normalizeUgotProRefundRequest)
);

export const normalizeUgotProCancelResult = (payload: unknown): UgotProCancelResult => {
  const row = record(payload, 'root');
  if (typeof row['immediate'] !== 'boolean') fail('immediate');
  if (typeof row['providerCanceled'] !== 'boolean') fail('providerCanceled');
  return {
    subscriptionId: integer(row['subscriptionId'], 'subscriptionId'),
    status: text(row['status'], 'status'),
    immediate: row['immediate'] as boolean,
    providerCanceled: row['providerCanceled'] as boolean,
    effectiveAt: optionalText(row['effectiveAt'], 'effectiveAt'),
    message: text(row['message'], 'message'),
  };
};

export const normalizeUgotProRefundActionResult = (payload: unknown): UgotProRefundActionResult => {
  const row = record(payload, 'root');
  return {
    refund: normalizeUgotProRefundRequest(row['refund']),
    orderStatus: optionalText(row['orderStatus'], 'orderStatus'),
    message: text(row['message'], 'message'),
  };
};
